import React, { useState } from "react";
import {
    User,
    Mail,
    Phone,
    Building2,
    Loader2,
    CheckCircle2,
    ArrowRight,
    Users,
    BookOpen,
    Brain,
    Clock,
    MessageCircle,
    Sparkles,
} from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";

// The parents-only counterpart of pages/webinar.
//
// Compulsory fields are unchanged (fullName / email / phone / school) — those
// still carry an asterisk. Everything below "Tell us a bit more" is optional
// (no asterisk) and the API defaults each unfilled answer to NULL, so leaving
// them all blank submits exactly as it always did.

const inputClass =
    "w-full border border-slate-200 bg-white rounded-lg pl-10 pr-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0] transition";

// A small header used to introduce each grouped question. The `required` dot is
// a red asterisk, matching the input labels.
function FieldLabel({ icon: Icon, text, required = false, hint }) {
    return (
        <div className="mb-2">
            <div className="flex items-center gap-2">
                <span className="text-[#2148C0]/70">
                    <Icon size={14} />
                </span>
                <span className="text-sm font-semibold text-slate-700">
                    {text}
                    {required && <span className="text-red-500 ml-0.5">*</span>}
                </span>
            </div>
            {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
        </div>
    );
}

// Icon-prefix wrapper for a plain <input>, same look as the original form.
function Field({ icon: Icon, children }) {
    return (
        <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2148C0]/60 pointer-events-none">
                <Icon size={16} />
            </div>
            {children}
        </div>
    );
}

// Rounded chip button — one shared style for both single- and multi-select
// groups. `selected` gets the brand fill, everything else stays a light border.
function Chip({ selected, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3.5 py-2 rounded-full border text-xs font-semibold transition-all ${selected
                    ? "bg-[#2148C0] border-[#2148C0] text-white shadow-sm"
                    : "bg-white border-slate-200 text-slate-600 hover:border-[#2148C0]/40 hover:text-[#2148C0]"
                }`}
        >
            {children}
        </button>
    );
}

// Fixed lists — kept small on purpose. Anything unusual goes into the Other box.
const CHILD_COUNT_OPTIONS = ["1", "2", "3", "4", "5+"];

const DIFFICULT_SUBJECT_OPTIONS = [
    "Mathematics",
    "English",
    "Physics",
    "Chemistry",
    "Biology",
    "Further Mathematics",
    "Economics",
    "Government",
    "Literature",
    "Geography",
];

const STRUGGLE_AREA_OPTIONS = [
    "Understanding concepts",
    "Remembering what they learned",
    "Completing assignments",
    "Concentration",
];

const HELP_FREQUENCY_OPTIONS = [
    "Daily",
    "A few times a week",
    "Weekly",
    "Occasionally",
    "Rarely",
    "Never",
];

const PLATFORM_WISH_OPTIONS = [
    "Practice questions",
    "Video lessons",
    "Progress tracking",
    "Homework help",
    "Teacher support",
    "Parent progress reports",
];

const ParentWebinarRegistrationPage = () => {
    const [formData, setFormData] = useState({
        // Compulsory
        fullName: "",
        email: "",
        phone: "",
        school: "",
        // Optional profile
        numChildren: "",
        difficultSubjects: [],
        difficultSubjectsOther: "",
        struggleAreas: [],
        struggleAreasOther: "",
        helpFrequency: "",
        biggestChallenge: "",
        platformWishes: [],
        platformWishesOther: "",
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    // Toggle a chip in a multi-select set. Storing arrays rather than sets so
    // the whole form serialises with a plain JSON.stringify.
    const toggleMulti = (field, value) => {
        setFormData((prev) => {
            const existing = prev[field] || [];
            const next = existing.includes(value)
                ? existing.filter((v) => v !== value)
                : [...existing, value];
            return { ...prev, [field]: next };
        });
    };

    // Single-select chip: tapping the active one clears it, so a parent who
    // ticked the wrong chip isn't stuck with it.
    const pickSingle = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field] === value ? "" : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        // Merge "Other" typed values into the multi-select arrays on submit —
        // the API stores one JSON array per question, and normaliseChoices on
        // the server drops empties for us.
        const mergeOther = (list, otherText) => {
            const other = (otherText || "").trim();
            return other ? [...list, other] : list;
        };

        const payload = {
            fullName: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            school: formData.school,
            numChildren: formData.numChildren || undefined,
            difficultSubjects: mergeOther(
                formData.difficultSubjects,
                formData.difficultSubjectsOther
            ),
            struggleAreas: mergeOther(
                formData.struggleAreas,
                formData.struggleAreasOther
            ),
            helpFrequency: formData.helpFrequency || undefined,
            biggestChallenge: formData.biggestChallenge || undefined,
            platformWishes: mergeOther(
                formData.platformWishes,
                formData.platformWishesOther
            ),
        };

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/parent-webinar`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            let data = {};
            try {
                data = await res.json();
            } catch {
                // response wasn't valid JSON (e.g. a proxy/404 error page)
            }

            if (!res.ok)
                throw new Error(data.message || "Something went wrong. Please try again.");
            setSubmitted(true);
        } catch (err) {
            setError(err.message || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#f8f7ff] via-[#eef2ff] to-[#f0f4ff] flex items-center justify-center px-4 py-16">
            <div className="w-full max-w-2xl">
                <div className="text-center mb-8">
                    <span className="inline-flex items-center gap-2 bg-blue-100 text-[#2148C0] px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                        <Users size={13} /> Free Parents' Webinar
                    </span>
                    <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mt-4 mb-3">
                        Reserve Your Spot at the{" "}
                        <span className="bg-gradient-to-r from-[#2148C0] to-purple-600 bg-clip-text text-transparent">
                            TeeChaa Parents' Webinar
                        </span>
                    </h1>
                    <p className="text-slate-500 text-sm md:text-base">
                        A session built for parents — how to support your child's learning,
                        follow their progress, and get the most out of TeeChaa at home. The
                        questions after your details are all optional but help us tailor the
                        session to what you actually need.
                    </p>
                </div>

                {submitted ? (
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 text-center border border-slate-100">
                        <div className="mx-auto mb-5 flex items-center justify-center rounded-full w-14 h-14 bg-green-50 border border-green-200">
                            <CheckCircle2 size={28} className="text-green-600" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-800 mb-2">You're registered!</h2>
                        <p className="text-slate-500 text-sm">
                            Check your inbox — we've sent a confirmation email to{" "}
                            <strong>{formData.email}</strong> with what to expect next.
                        </p>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-6 border border-slate-100"
                    >
                        {/* Compulsory section — unchanged shape, asterisks visible */}
                        <div className="space-y-4">
                            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">
                                Your details <span className="text-red-500">*</span>
                            </p>

                            <Field icon={User}>
                                <input
                                    name="fullName"
                                    placeholder="Your full name *"
                                    required
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    className={inputClass}
                                />
                            </Field>

                            <Field icon={Mail}>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email address *"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={inputClass}
                                />
                            </Field>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field icon={Phone}>
                                    <input
                                        type="tel"
                                        name="phone"
                                        placeholder="Phone number *"
                                        required
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className={inputClass}
                                    />
                                </Field>

                                <Field icon={Building2}>
                                    <input
                                        name="school"
                                        placeholder="Child's school *"
                                        required
                                        value={formData.school}
                                        onChange={handleChange}
                                        className={inputClass}
                                    />
                                </Field>
                            </div>
                        </div>

                        {/* Optional section */}
                        <div className="border-t border-slate-100 pt-6 space-y-6">
                            <div>
                                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">
                                    Tell us a bit more
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                    All optional — skip anything that doesn't apply.
                                </p>
                            </div>

                            {/* Number of children */}
                            <div>
                                <FieldLabel icon={Users} text="How many children do you have?" />
                                <div className="flex flex-wrap gap-2">
                                    {CHILD_COUNT_OPTIONS.map((n) => (
                                        <Chip
                                            key={n}
                                            selected={formData.numChildren === n}
                                            onClick={() => pickSingle("numChildren", n)}
                                        >
                                            {n}
                                        </Chip>
                                    ))}
                                </div>
                            </div>

                            {/* Difficult subjects */}
                            <div>
                                <FieldLabel
                                    icon={BookOpen}
                                    text="What subjects do your children have the most difficulty with?"
                                    hint="Pick as many as apply."
                                />
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {DIFFICULT_SUBJECT_OPTIONS.map((s) => (
                                        <Chip
                                            key={s}
                                            selected={formData.difficultSubjects.includes(s)}
                                            onClick={() => toggleMulti("difficultSubjects", s)}
                                        >
                                            {s}
                                        </Chip>
                                    ))}
                                </div>
                                <input
                                    name="difficultSubjectsOther"
                                    placeholder="Other subject(s)…"
                                    value={formData.difficultSubjectsOther}
                                    onChange={handleChange}
                                    className="w-full border border-slate-200 bg-white rounded-lg px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0] transition"
                                />
                            </div>

                            {/* Struggle areas */}
                            <div>
                                <FieldLabel
                                    icon={Brain}
                                    text="What areas of learning does your child struggle with the most?"
                                    hint="Pick as many as apply."
                                />
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {STRUGGLE_AREA_OPTIONS.map((a) => (
                                        <Chip
                                            key={a}
                                            selected={formData.struggleAreas.includes(a)}
                                            onClick={() => toggleMulti("struggleAreas", a)}
                                        >
                                            {a}
                                        </Chip>
                                    ))}
                                </div>
                                <input
                                    name="struggleAreasOther"
                                    placeholder="Other area(s)…"
                                    value={formData.struggleAreasOther}
                                    onChange={handleChange}
                                    className="w-full border border-slate-200 bg-white rounded-lg px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0] transition"
                                />
                            </div>

                            {/* Help frequency */}
                            <div>
                                <FieldLabel
                                    icon={Clock}
                                    text="How often do you help your child with their schoolwork?"
                                />
                                <div className="flex flex-wrap gap-2">
                                    {HELP_FREQUENCY_OPTIONS.map((f) => (
                                        <Chip
                                            key={f}
                                            selected={formData.helpFrequency === f}
                                            onClick={() => pickSingle("helpFrequency", f)}
                                        >
                                            {f}
                                        </Chip>
                                    ))}
                                </div>
                            </div>

                            {/* Biggest challenge */}
                            <div>
                                <FieldLabel
                                    icon={MessageCircle}
                                    text="What is the biggest challenge you face when supporting your child's education?"
                                />
                                <textarea
                                    name="biggestChallenge"
                                    placeholder="In your own words…"
                                    rows={3}
                                    value={formData.biggestChallenge}
                                    onChange={handleChange}
                                    className="w-full border border-slate-200 bg-white rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0] transition resize-none"
                                />
                            </div>

                            {/* Platform wishes */}
                            <div>
                                <FieldLabel
                                    icon={Sparkles}
                                    text="What would you most like to see in a learning platform for your child?"
                                    hint="Pick as many as apply."
                                />
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {PLATFORM_WISH_OPTIONS.map((w) => (
                                        <Chip
                                            key={w}
                                            selected={formData.platformWishes.includes(w)}
                                            onClick={() => toggleMulti("platformWishes", w)}
                                        >
                                            {w}
                                        </Chip>
                                    ))}
                                </div>
                                <input
                                    name="platformWishesOther"
                                    placeholder="Other…"
                                    value={formData.platformWishesOther}
                                    onChange={handleChange}
                                    className="w-full border border-slate-200 bg-white rounded-lg px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0] transition"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full flex items-center justify-center gap-2 text-white font-semibold py-3.5 rounded-xl transition-all ${loading
                                    ? "opacity-70 cursor-not-allowed"
                                    : "hover:shadow-lg hover:-translate-y-0.5"
                                }`}
                            style={{ background: "linear-gradient(135deg, #2148C0, #6C63FF)" }}
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Registering...
                                </>
                            ) : (
                                <>
                                    Register for the Parents' Webinar
                                    <ArrowRight size={16} />
                                </>
                            )}
                        </button>

                        <p className="text-center text-slate-400 text-xs">
                            No spam. We'll only email you about this webinar.
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ParentWebinarRegistrationPage;
